import { useMemo, useState } from 'react'
import { ApiRequestError } from '../../../lib/api'
import {
  type EducationEntry,
  useOnboardingV2Store,
} from '../../../stores/onboardingV2Store'
import OnboardingLayout from './OnboardingLayout'
import PrimaryButton from './PrimaryButton'
import { ERROR_CLASS, HINT_CLASS, INPUT_CLASS, LABEL_CLASS } from './formStyles'
import { stepsForMode } from './steps'

function formatEducationLine(e: EducationEntry): string {
  return [e.degree, e.institution, e.year].filter(Boolean).join(', ')
}

function parseEducation(text: string): EducationEntry[] {
  if (!text.trim()) return []
  const isYear = (s: string) => /^\d{4}$/.test(s)
  return text
    .split('\n')
    .filter((l) => l.trim())
    .map((line) => {
      const parts = line.split(',').map((p) => p.trim()).filter(Boolean)
      if (parts.length >= 3) {
        return { degree: parts[0], institution: parts[1], year: Number(parts[2]) || null }
      }
      if (parts.length === 2) {
        return isYear(parts[1])
          ? { institution: parts[0], year: Number(parts[1]) }
          : { degree: parts[0], institution: parts[1] }
      }
      return { institution: parts[0] || line.trim() }
    })
}

export default function ConfirmProfileScreen() {
  const capabilityMode = useOnboardingV2Store((s) => s.state?.capability_mode ?? 'managed')
  const prefill = useOnboardingV2Store((s) => s.state?.profile_prefill)
  const extractionStatus = useOnboardingV2Store((s) => s.state?.extraction?.status)
  const saveProfile = useOnboardingV2Store((s) => s.saveProfile)

  const initialEducation = useMemo(
    () => (prefill?.education ?? []).map(formatEducationLine).join('\n'),
    [prefill?.education],
  )
  const initialLanguages = useMemo(() => (prefill?.languages ?? []).join('\n'), [prefill?.languages])

  const [fullName, setFullName] = useState(prefill?.full_name ?? '')
  const [email, setEmail] = useState(prefill?.email ?? '')
  const [phone, setPhone] = useState(prefill?.phone ?? '')
  const [linkedin, setLinkedin] = useState(prefill?.linkedin_url ?? '')
  const [location, setLocation] = useState(prefill?.location ?? '')
  const [educationText, setEducationText] = useState(initialEducation)
  const [languagesText, setLanguagesText] = useState(initialLanguages)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const confidence = prefill?.confidence ?? {}
  const lowConf = (field: string) => confidence[field] === 'low'
  const canSubmit = fullName.trim().length > 0 && email.trim().length > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      await saveProfile({
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        linkedin_url: linkedin.trim() || null,
        location: location.trim() || null,
        education: parseEducation(educationText),
        languages: languagesText
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean),
      })
      // saveProfile() refreshes state; the page re-renders to Review or Extraction Wait.
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Could not save your profile.')
      setSaving(false)
    }
  }

  const extractionReady = extractionStatus === 'ready'

  return (
    <OnboardingLayout steps={stepsForMode(capabilityMode)} activeKey="profile">
      <div className="mx-auto w-full max-w-[600px]">
        <header className="mb-6 text-center">
          <h1 className="font-serif text-[28px] font-semibold text-ob-ink md:text-3xl">
            Confirm your profile
          </h1>
          <p className="mt-2 font-body text-base text-ob-secondary">
            We filled these in from your material. Review and fix anything — these details appear at
            the top of every generated CV.
          </p>
        </header>

        {/* Background extraction indicator */}
        <div className="mb-6 flex items-center justify-center">
          <span
            className={[
              'inline-flex items-center gap-2 border px-3 py-1.5 font-body text-xs',
              extractionReady
                ? 'border-ob-accent/40 text-ob-ink'
                : 'border-ob-secondary/30 text-ob-secondary',
            ].join(' ')}
          >
            {extractionReady ? (
              <span className="material-symbols-outlined text-[16px] text-ob-accent" aria-hidden="true">
                check_circle
              </span>
            ) : (
              <span className="h-3 w-3 animate-spin rounded-full border border-ob-secondary/40 border-t-ob-secondary" />
            )}
            {extractionReady ? 'Done reading your CVs — ready to review' : 'Reading your CVs in the background…'}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="ob-name" className={LABEL_CLASS}>Full name *</label>
            <input id="ob-name" type="text" required value={fullName}
              onChange={(e) => setFullName(e.target.value)} className={INPUT_CLASS} />
            {lowConf('full_name') && <p className={HINT_CLASS}>Worth double-checking.</p>}
          </div>

          <div>
            <label htmlFor="ob-email" className={LABEL_CLASS}>Email *</label>
            <input id="ob-email" type="email" inputMode="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className={INPUT_CLASS} />
            {lowConf('email') && <p className={HINT_CLASS}>Worth double-checking.</p>}
          </div>

          {/* Sibling fields — two columns on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label htmlFor="ob-phone" className={LABEL_CLASS}>Phone</label>
              <input id="ob-phone" type="tel" inputMode="tel" value={phone}
                onChange={(e) => setPhone(e.target.value)} className={INPUT_CLASS} />
            </div>
            <div>
              <label htmlFor="ob-location" className={LABEL_CLASS}>Location</label>
              <input id="ob-location" type="text" value={location} placeholder="City, Country"
                onChange={(e) => setLocation(e.target.value)} className={INPUT_CLASS} />
            </div>
          </div>

          <div>
            <label htmlFor="ob-linkedin" className={LABEL_CLASS}>LinkedIn</label>
            <input id="ob-linkedin" type="text" inputMode="url" value={linkedin}
              placeholder="linkedin.com/in/…"
              onChange={(e) => setLinkedin(e.target.value)} className={INPUT_CLASS} />
          </div>

          <div>
            <label htmlFor="ob-education" className={LABEL_CLASS}>Education (one entry per line)</label>
            <textarea id="ob-education" rows={3} value={educationText}
              placeholder="BSc Computer Science, University of Example, 2015"
              onChange={(e) => setEducationText(e.target.value)} className={`${INPUT_CLASS} resize-y`} />
            <p className={HINT_CLASS}>Format: Degree, Institution, Year.</p>
          </div>

          <div>
            <label htmlFor="ob-languages" className={LABEL_CLASS}>Languages (one per line)</label>
            <textarea id="ob-languages" rows={2} value={languagesText}
              placeholder={'English (native)\nHebrew (fluent)'}
              onChange={(e) => setLanguagesText(e.target.value)} className={`${INPUT_CLASS} resize-y`} />
          </div>

          {error && <p className={ERROR_CLASS} role="alert">{error}</p>}

          <div className="pt-2">
            <PrimaryButton type="submit" className="w-full" disabled={!canSubmit || saving}>
              {saving ? 'Saving…' : 'Save and continue'}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </OnboardingLayout>
  )
}
